import { useState } from "react";
import { api, type FeedbackType } from "../lib/api";

const TITLE_MAX = 200;
const BODY_MAX = 4000;

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal for submitting feedback or a feature request from the floating
 * Feedback button. Posts to /api/feedback as the authenticated user.
 */
export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>("feature_request");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setTitle("");
    setBody("");
    setError(null);
    setSuccess(null);
    setBusy(false);
  }

  async function submit() {
    setError(null);
    setSuccess(null);
    if (!title.trim() || !body.trim()) {
      setError("Title and body are both required.");
      return;
    }
    if (title.length > TITLE_MAX) {
      setError(`Title must be at most ${TITLE_MAX} characters.`);
      return;
    }
    if (body.length > BODY_MAX) {
      setError(`Body must be at most ${BODY_MAX} characters.`);
      return;
    }
    setBusy(true);
    try {
      const created = await api.feedback.submit({ type, title, body });
      setSuccess(
        type === "feature_request"
          ? `Submitted — feature request \"${created.title}\" is now on the public board.`
          : `Submitted — admins can see your feedback in the Feedback tab.`,
      );
      setTitle("");
      setBody("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
      onClick={() => {
        reset();
        onClose();
      }}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Send feedback</h2>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded-md text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <fieldset>
            <legend className="mb-1 text-sm font-semibold">Type</legend>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="feedback-type"
                  value="feature_request"
                  checked={type === "feature_request"}
                  onChange={() => setType("feature_request")}
                />
                Feature request <span className="text-xs text-slate-500">(public, voteable)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="feedback-type"
                  value="feedback"
                  checked={type === "feedback"}
                  onChange={() => setType("feedback")}
                />
                Feedback <span className="text-xs text-slate-500">(admin-only)</span>
              </label>
            </div>
          </fieldset>

          <div>
            <label htmlFor="fb-title" className="mb-1 block text-sm font-semibold">
              Title
            </label>
            <input
              id="fb-title"
              type="text"
              maxLength={TITLE_MAX}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="One-line summary"
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-slate-500">
              {title.length} / {TITLE_MAX}
            </p>
          </div>

          <div>
            <label htmlFor="fb-body" className="mb-1 block text-sm font-semibold">
              Details
            </label>
            <textarea
              id="fb-body"
              rows={6}
              maxLength={BODY_MAX}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe what you'd like and why."
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-slate-500">
              {body.length} / {BODY_MAX}
            </p>
          </div>

          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}
          {success && (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {success}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
